import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { LoanProductCard } from "../../../../../components/cards/loanProduct";
import { AddLoanProductModal } from "../../../../../components/modals/app_modals/loans/configure";
import { EmptyState } from "../../../../../components/states/emptyState";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { useGetCooperativeLoanConfigQuery } from "../../../../../redux/features/loans/loans.api";
import { LoanProduct } from "../../../../../redux/features/loans/loans.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";

export const LoanConfiguration = () => {
  const [selectedProduct, setSelectedProduct] = useState<LoanProduct>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: loansConfig,
    isFetching: fetchingConfig,
    refetch: refetchConfig,
  } = useGetCooperativeLoanConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <ContainerCard className="!px-0">
      <div
        className="
        w-full flex flex-row gap-4 items-center justify-between
        mb-6 px-4 md:px-8
        "
      >
        <p className="font-bold text-base">Loan Settings</p>

        <div className="!w-full !max-w-[170px] md:!max-w-[174px]">
          <Button
            text={"Add a loan product"}
            onClick={() => {
              handleModalOpen("loanProductModal");
            }}
            icon="plus"
            type="button"
            className="!min-w-[80px] !max-w-fit"
            size="small"
            loading={false}
            disabled={false}
            variation="primary"
          />
        </div>
      </div>

      <div className="px-4 md:px-8">
        {fetchingConfig ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {[1, 2, 3, 4].map((v) => {
              return (
                <div
                  className="border border-pry-dark-green-300/10 rounded-lg flex flex-col gap-6 p-4"
                  key={`${v}`}
                >
                  <Skeleton
                    width={"100%"}
                    height={30}
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  />

                  <div className="w-full flex items-center justify-between gap-2">
                    <Skeleton
                      width={"47px"}
                      height={20}
                      baseColor={SKELETON_BASE_COLOR}
                      highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    />

                    <Skeleton
                      width={"52px"}
                      height={20}
                      baseColor={SKELETON_BASE_COLOR}
                      highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : undefined}

        {[0, undefined, null].includes(loansConfig?.data?.length) &&
        !fetchingConfig ? (
          <EmptyState
            image={
              <img
                src={"/assets/images/empty_state_loan.svg"}
                alt="approvals"
              />
            }
            title="No active loan product yet"
            titleClassName="md:text-base"
            subtitle=""
            subtitleClassName=""
            actionText={"Add a loan product"}
            onClick={() => {
              handleModalOpen("loanProductModal");
            }}
            actionButtonVariant="secondary"
            buttonContainerClassName="!max-w-[174px]"
          />
        ) : undefined}

        {![0, undefined, null].includes(loansConfig?.data?.length) &&
        !fetchingConfig ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {loansConfig?.data?.map((product, idx) => {
              const key = `loanProduct-${idx}`;
              return (
                <LoanProductCard
                  productName={product?.LoanName}
                  creationDate={product?.DateAdded}
                  onClick={() => {
                    setSelectedProduct(product);
                    handleModalOpen("loanProductModal");
                  }}
                  key={key}
                />
              );
            })}
          </div>
        ) : undefined}
      </div>

      <AddLoanProductModal
        modalOpen={modal === "loanProductModal" && modalOpen}
        onModalClose={() => {
          handleModalClose();
          setSelectedProduct(undefined);
        }}
        mode={selectedProduct ? "edit" : "create"}
        selectedProduct={selectedProduct}
        refetch={() => {
          refetchConfig();
        }}
      />
    </ContainerCard>
  );
};
